import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, ShieldCheck, ClipboardCheck, KeyRound, Eye, X } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";

const POINTS = [
    { icon: Lock, title: "Secure Project Handling", desc: "Your project files are handled responsibly throughout the production process." },
    { icon: ShieldCheck, title: "Content Protection", desc: "Private project content is not intentionally shared without authorization." },
    { icon: ClipboardCheck, title: "Client Approval", desc: "Where applicable, final content can be reviewed before publishing." },
    { icon: KeyRound, title: "Controlled Access", desc: "Project files and links are shared only with authorized parties." },
    { icon: Eye, title: "Transparent Process", desc: "You know where and how your 360° experience will be used." },
];

export default function Privacy() {
    const [open, setOpen] = useState(false);

    return (
        <section className="relative py-20 lg:py-32" data-testid="privacy-section">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
                    <div>
                        <SectionHead
                            chapter="10"
                            eyebrow="Trust"
                            title="YOUR PRIVACY MATTERS"
                            sub="We respect your privacy and handle your business images, 360° content and project information responsibly."
                        />
                        <Reveal delay={0.2}>
                            <button
                                data-testid="privacy-policy-btn"
                                onClick={() => setOpen(true)}
                                className="mt-8 rounded-full border border-[#00C6FF]/50 px-7 py-3.5 text-xs font-bold uppercase tracking-[0.15em] text-[#00C6FF] transition-colors hover:bg-[#00C6FF]/10"
                            >
                                Read Privacy Policy
                            </button>
                        </Reveal>
                    </div>
                    <div className="space-y-4">
                        {POINTS.map((p, i) => (
                            <motion.div
                                key={p.title}
                                data-testid={`privacy-point-${i}`}
                                initial={{ opacity: 0, x: 32 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.6, delay: i * 0.07 }}
                                className="glass-card flex items-start gap-5 rounded-2xl p-5"
                            >
                                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-[#00C6FF]/40 text-[#00C6FF]">
                                    <p.icon size={20} />
                                </span>
                                <div>
                                    <h3 className="font-semibold text-white">{p.title}</h3>
                                    <p className="mt-1 text-sm text-slate-400">{p.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[70] flex items-center justify-center bg-[#050B14]/85 p-4 backdrop-blur-md"
                        onClick={() => setOpen(false)}
                        data-testid="privacy-modal"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 40, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 40, scale: 0.96 }}
                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            className="glass-card max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-2xl p-8"
                        >
                            <div className="flex items-start justify-between">
                                <h3 className="text-2xl font-bold text-white">Privacy Policy</h3>
                                <button
                                    data-testid="privacy-modal-close"
                                    onClick={() => setOpen(false)}
                                    className="rounded-full border border-[#00C6FF]/30 p-2 text-slate-300 hover:text-[#00C6FF]"
                                    aria-label="Close privacy policy"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                            <div className="mt-5 space-y-4 text-sm leading-relaxed text-slate-300">
                                <p>Prime 360 View ("we", "our") respects the privacy of every client and visitor. This policy explains how we handle your information and project content.</p>
                                <p><strong className="text-white">Information we collect.</strong> When you request a quote we collect your name, phone number, email and business details solely to respond to your enquiry and deliver your project.</p>
                                <p><strong className="text-white">Project content.</strong> 360° imagery and project files captured at your premises are used only to produce your virtual tour. Private content is never shared or published without your authorization, and final content can be reviewed before publishing where applicable.</p>
                                <p><strong className="text-white">Controlled access.</strong> Project files and tour links are shared only with authorized parties. You decide where your 360° experience appears — your website, Google, QR codes or private links.</p>
                                <p><strong className="text-white">Data handling.</strong> We do not sell your personal information. Enquiry data is stored securely and retained only as long as needed to serve you.</p>
                                <p><strong className="text-white">Contact.</strong> Questions about this policy: info@prime360view.in or +91 77097 08074.</p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
